/**
 * Kafelki z GET /kontrola-korekt/podsumowanie (liczby + brutto_do_dodania) i
 * tabela "Per sklep" (per_sklep). Klik w kafelek z filtrem ustawia filtr tabeli;
 * klik w wiersz sklepu ustawia sklep + filtr "do_dodania".
 * "Bez pozycji" (status 'nowa') nie ma filtra w /lista (kontrakt) - kafelek nieklikalny.
 */
import React from 'react';
import { formatujKwote } from './format';

const Kafelek = ({ id, etykieta, wartosc, dopisek, kolor, tlo, filtr, aktywny, onFiltr }) => {
  const klikalny = Boolean(filtr && onFiltr);
  return (
    <div
      data-testid={`kafelek-${id}`}
      role={klikalny ? 'button' : undefined}
      tabIndex={klikalny ? 0 : undefined}
      onClick={klikalny ? () => onFiltr(filtr) : undefined}
      onKeyDown={klikalny ? (e) => { if (e.key === 'Enter' || e.key === ' ') onFiltr(filtr); } : undefined}
      style={{
        flex: '1 1 120px', minWidth: '120px', padding: '8px 10px', borderRadius: '6px',
        backgroundColor: tlo, borderLeft: `3px solid ${kolor}`,
        outline: aktywny ? `2px solid ${kolor}` : 'none',
        cursor: klikalny ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: '18px', fontWeight: 700, color: kolor, lineHeight: 1.2 }}>{wartosc}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>{etykieta}</div>
      {dopisek && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{dopisek}</div>}
    </div>
  );
};

const PodsumowanieKorekt = ({ liczby, bruttoDoDodania, aktywnyFiltr, onFiltr }) => {
  const l = liczby || {};
  const n = (k) => (l[k] === null || l[k] === undefined ? '—' : l[k]);

  const kafelki = [
    { id: 'wszystkie', etykieta: 'Wszystkie', wartosc: n('wszystkie'), kolor: 'var(--primary)', tlo: 'var(--primary-light)', filtr: 'wszystkie' },
    { id: 'do_dodania', etykieta: 'Do dodania', wartosc: n('do_dodania'), dopisek: formatujKwote(bruttoDoDodania), kolor: 'var(--success)', tlo: 'var(--success-light)', filtr: 'do_dodania' },
    { id: 'brak_faktury', etykieta: 'Brak faktury źródłowej', wartosc: n('brak_faktury'), kolor: 'var(--danger)', tlo: 'var(--danger-light)', filtr: 'brak_faktury' },
    { id: 'faktura_poza_systemem', etykieta: 'Faktura poza systemem', wartosc: n('faktura_poza_systemem'), kolor: 'var(--warning)', tlo: 'var(--warning-light)', filtr: 'faktura_poza_systemem' },
    { id: 'brak_produktow', etykieta: 'Brak produktów', wartosc: n('brak_produktow'), kolor: 'var(--danger)', tlo: 'var(--danger-light)', filtr: 'brak_produktow' },
    { id: 'dodane', etykieta: 'Dodane', wartosc: n('dodane'), kolor: 'var(--info)', tlo: 'var(--info-light)', filtr: 'dodane' },
    { id: 'pominiete', etykieta: 'Pominięte', wartosc: n('pominiete'), kolor: 'var(--text-secondary)', tlo: 'var(--bg)', filtr: 'pominiete' },
    { id: 'bez_pozycji', etykieta: 'Bez pozycji', wartosc: n('bez_pozycji'), dopisek: 'kliknij „Pobierz pozycje z SFD”', kolor: 'var(--text-muted)', tlo: 'var(--bg)' },
  ];

  return (
    <div data-testid="podsumowanie-korekt" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '12px' }}>
      {kafelki.map(k => <Kafelek key={k.id} {...k} aktywny={Boolean(k.filtr) && k.filtr === aktywnyFiltr} onFiltr={onFiltr} />)}
    </div>
  );
};

const th = { padding: '5px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px' };
const prawa = { ...td, textAlign: 'right' };

/** Tabela per sklep: [{sklep_id, nazwa, wszystkie, do_dodania, dodane, brutto}]. */
export const PerSklepKorekt = ({ perSklep, aktywnySklepId, onWybierz }) => {
  const lista = Array.isArray(perSklep) ? perSklep : [];
  return (
    <div className="card" data-testid="per-sklep-korekt" style={{ fontSize: '12px' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600 }}>Per sklep (wg adresu dostawy z SFD)</h3>
      {lista.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Brak danych — korekty pojawią się po synchronizacji z SFD</div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Sklep</th>
                <th style={{ ...th, textAlign: 'right' }}>Wszystkie</th>
                <th style={{ ...th, textAlign: 'right' }}>Do dodania</th>
                <th style={{ ...th, textAlign: 'right' }}>Dodane</th>
                <th style={{ ...th, textAlign: 'right' }}>Brutto do dodania</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((w, idx) => {
                const nieznany = w.sklep_id === null || w.sklep_id === undefined;
                const id = nieznany ? 0 : w.sklep_id;
                const aktywny = aktywnySklepId !== null && aktywnySklepId !== undefined && aktywnySklepId === id;
                return (
                  <tr
                    key={`${id}-${w.nazwa || idx}`}
                    data-testid="per-sklep-korekt-wiersz"
                    role="button"
                    tabIndex={0}
                    title="Kliknij, żeby pokazać korekty do dodania w tym sklepie"
                    onClick={() => onWybierz && onWybierz(id, w)}
                    onKeyDown={(e) => { if (onWybierz && (e.key === 'Enter' || e.key === ' ')) onWybierz(id, w); }}
                    style={{ cursor: 'pointer', backgroundColor: aktywny ? 'var(--primary-light)' : (nieznany ? 'var(--warning-light)' : 'transparent') }}
                  >
                    <td style={{ ...td, fontWeight: 600, color: nieznany ? 'var(--warning)' : 'var(--text)', whiteSpace: 'nowrap' }}>{w.nazwa || 'nieznany adres'}</td>
                    <td style={prawa}>{w.wszystkie ?? '—'}</td>
                    <td style={{ ...prawa, fontWeight: 600, color: w.do_dodania > 0 ? 'var(--success)' : 'var(--text-muted)' }}>{w.do_dodania ?? '—'}</td>
                    <td style={prawa}>{w.dodane ?? '—'}</td>
                    <td style={{ ...prawa, whiteSpace: 'nowrap' }}>{formatujKwote(w.brutto)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PodsumowanieKorekt;
